const SCRIPT_ID = 'wnhub-recaptcha-script'
const CONTAINER_ID = 'wnhub-recaptcha-container'
const LOAD_CALLBACK = 'wnhubRecaptchaLoadHandler'
const DEBUG_WIDGET_ID = -1
const DEBUG_TOKEN = 'wnhub_debug_recaptcha_token'

declare global {
  interface Window {
    grecaptcha: any
    [LOAD_CALLBACK]?: Function
  }
}

const isDebug = computed(() => !useRuntimeConfig().public.recaptchaSiteKey)
const isLoaded = ref(false)
const widgetId = ref<number | null>(null)
const isRendered = computed(() => widgetId.value !== null)
const token = ref<string | null>(null)

let loadPromise: Promise<void> | null = null
let successHandler: Function | null = null
let errorHandler: Function | null = null

function load(): Promise<void> {
  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    if (!process.client) {
      throw new Error('reCAPTCHA is not supported on the server')
    }

    if (isDebug.value) {
      isLoaded.value = true
      return resolve()
    }

    if (isLoaded.value || document.getElementById(SCRIPT_ID)) {
      // during loading and successful load, the function returns a promise
      // on error, the load flag is reset, the script is removed
      // i.e., under normal conditions this condition should not be met
      throw new Error('reCAPTCHA has already been loaded')
    }

    window[LOAD_CALLBACK] = () => {
      try {
        window.grecaptcha.ready(() => {
          isLoaded.value = true
          resolve()
        })
      } catch (e) {
        resetLoading()
        reject(e)
      }
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit&onload=${LOAD_CALLBACK}`
    script.async = true
    script.onerror = () => {
      resetLoading()
      reject(new Error('reCAPTCHA failed to load'))
    }
    document.body.appendChild(script)
  })

  return loadPromise
}

function resetLoading(): void {
  loadPromise = null
  document.getElementById(SCRIPT_ID)?.remove()
  window[LOAD_CALLBACK] = () => {}
}

function render(): void {
  if (!isLoaded.value) {
    throw new Error('reCAPTCHA is not loaded')
  }

  if (isRendered.value) {
    return
  }

  if (isDebug.value) {
    widgetId.value = DEBUG_WIDGET_ID
    return
  }

  const container = document.createElement('div')
  container.id = CONTAINER_ID
  document.body.appendChild(container)

  widgetId.value = window.grecaptcha.render(container, {
    size: 'invisible',
    sitekey: useRuntimeConfig().public.recaptchaSiteKey,
    callback: onSuccess,
    'error-callback': onError,
  })
}

function execute(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isRendered.value) {
      throw new Error('reCAPTCHA widget is not rendered')
    }

    reset()

    successHandler = resolve
    errorHandler = reject

    if (isDebug.value) {
      onSuccess(DEBUG_TOKEN)
    } else {
      window.grecaptcha.execute(widgetId.value)
    }
  })
}

function reset(): void {
  if (errorHandler) {
    errorHandler(new RecaptchaReset())
  }

  resetToken()
  resetHandlers()

  if (!isDebug.value) {
    window.grecaptcha.reset(widgetId.value)
  }
}

function resetToken(): void {
  token.value = null
}

function resetHandlers(): void {
  successHandler = null
  errorHandler = null
}

function onSuccess(newToken: string): void {
  token.value = newToken

  if (successHandler) {
    successHandler()
  }

  resetHandlers()
}

function onError(e: any): void {
  e ??= new Error('reCAPTCHA error')

  const handler = errorHandler

  resetToken()
  resetHandlers()

  if (handler) {
    handler(e)
  } else {
    throw e
  }
}

export class RecaptchaReset extends Error {
  constructor() {
    super()
    this.name = 'RecaptchaReset'
  }
}

export default function useRecaptcha() {
  return {
    isDebug: readonly(isDebug),
    isLoaded: readonly(isLoaded),
    isRendered,
    token: readonly(token),
    load,
    render,
    execute,
    reset,
  }
}
